import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { t } from 'utils';
import ReloginPopup from 'components/SessionProlongPopup/ReloginPopup';
import ModalPopup from 'components/ModalPopup';
import Button from 'components/Button/Button';
import { apiToken } from 'utils/Api/Request';
import useApiProlongSession from 'apiRequests/useApiProlongSession';
import useApiLogin from 'apiRequests/useApiLogin';
import styles from 'components/SessionProlongPopup/SessionProlongPopup.module.scss';
import { useSelector } from 'react-redux';
import { getCurrentUser } from '../../selectors/userSelectors';

function parseJwt(token) {
  if (!token) {
    return null;
  }

  // Split token and get public part
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace('-', '+').replace('_', '/');

  return JSON.parse(window.atob?.(base64));
}

const SHOW_MODAL_TIME = (1 * 60 * 1000 /* 1 min */);
const CHECK_TOKEN_INTERVAL = 10_000;
const HIDE_MODAL_INTERVAL = 2_000;

const isModalAllowed = () => {
  if (location.origin.indexOf('admin.') > 0) return false;
  if (location.href.indexOf('/login') > -1) return false;
  return true;
};

function saveSession(apiAuth) {
  localStorage.setItem('apiAuth', JSON.stringify({
    jwtToken: apiAuth.jwtToken,
    token: apiAuth.authToken,
    user: apiAuth.userId,
    userType: apiAuth.userType,
  }));
}

export default function SessionProlongPopup() {
  const user = useSelector(getCurrentUser);
  const { current: self } = useRef({
    taskBackground: null,
    taskModalOpened: null,
    taskTimeLeft: null,
    taskToOpenModal: null,
    openModalExp: null,
    exp: Date.now(),
  });
  const [showModalTime] = useState(SHOW_MODAL_TIME);
  const { prolongJwtSession } = useApiProlongSession();
  const { login } = useApiLogin();
  const [visible, setVisible] = useState(false);
  const [expired, setExpired] = useState(false);
  const onClose = useCallback(() => null, []);
  const [momentTimeLeft, setTimeLeft] = useState(0);
  const [loginModalVisible, setLoginModalVisible] = useState(false);

  const onLogout = useCallback(() => {
    if (location.origin.indexOf('admin.') > 0) {
      return;
    }
    localStorage.removeItem('apiAuth');
    if (location.href.indexOf('/login') < 0) {
        location.href = '/login?expired=1';
    }
  }, []);

  const onProlong = useCallback(async () => {
    const { apiAuth } = await prolongJwtSession();
    saveSession(apiAuth);
    setVisible(false);
  }, []);

  const onLogin = useCallback(async (formData) => {
    const { apiAuth } = await login(formData);
    saveSession(apiAuth);
    setLoginModalVisible(false);
    setVisible(false);
    setExpired(false);
  });

  // Handle SSO user logout when token expires or login modal is shown
  useEffect(() => {
    if ((expired || loginModalVisible) && user.isSSO) {
      onLogout();
    }
  }, [expired, loginModalVisible, user.isSSO, onLogout]);

  // Background check
  useEffect(() => {
    self.taskBackground = setInterval(() => {
      const { exp } = parseJwt(apiToken.getJwtToken(true)) || {};
      if (!exp) {
        if (isModalAllowed()) {
          setVisible(true);
          setExpired(true);
        }
        return;
      }
      self.exp = exp * 1000;
      // milliseconds
      const timeLeft = exp * 1000 - Date.now();
      if (timeLeft < 0) {
        setVisible(exp);
        setExpired(true);

        return;
      }

      setExpired(false);

      if (timeLeft < showModalTime) {
        setVisible(exp);
      } else if (self.openModalExp !== exp) {
        self.openModalExp = exp;
        clearTimeout(self.taskToOpenModal);
        const delay = timeLeft - showModalTime;
        self.taskToOpenModal = setTimeout(() => {
          self.taskToOpenModal = null;
          const { exp: currentExp } = parseJwt(apiToken.getJwtToken(true));
          // do not bother user if he loggen in different tab
          if (!currentExp) {
            return;
          }
          if ((currentExp * 1000 - Date.now()) < 0) {
            setExpired(true);
          }
          setVisible(currentExp);
        }, delay);
      }
    }, CHECK_TOKEN_INTERVAL);

    return () => {
      clearInterval(self.taskBackground);
      self.taskBackground = null;
    };
  }, [showModalTime]);

  // Modal opened, check token often
  useEffect(() => {
    if (!visible) {
      clearInterval(self.taskModalOpened);
      self.taskModalOpened = null;

      return () => null;
    }

    // use only one interval task at once;
    if (self.taskModalOpened) {
      clearInterval(self.taskModalOpened);
    }

    self.taskModalOpened = setInterval(() => {
      const { exp } = parseJwt(apiToken.getJwtToken(true)) || {};
      if (!exp) {
        // don't show modal on login page or admin
        if (isModalAllowed()) {
          setVisible(true);
          setExpired(true);
        }

        return;
      }
      self.exp = exp * 1000;
      const timeLeft = exp * 1000 - Date.now();
      if (timeLeft < 0) {
        setVisible(exp);
        setExpired(true);

        return;
      }

      setExpired(false);
      setLoginModalVisible(false);
      if (timeLeft > showModalTime) {
        setVisible(false);

        clearInterval(self.taskModalOpened);
        self.taskModalOpened = null;
        return;
      }

      setVisible(exp);
    }, HIDE_MODAL_INTERVAL);

    return () => {
      clearInterval(self.taskModalOpened);
      self.taskModalOpened = null;
    };
  }, [visible, showModalTime]);

  useEffect(() => {
    if (!visible) {
      setTimeLeft(0);
      clearInterval(self.taskTimeLeft);
      return;
    }
    self.taskTimeLeft = setInterval(() => {
      setTimeLeft(moment(self.exp).fromNow());
    }, 200);
  }, [visible]);

  // For non-SSO users, show the ReloginPopup when loginModalVisible or expired is true
  // For SSO users, the useEffect above will handle logout
  if ((loginModalVisible || expired) && !user.isSSO) {
    return <ReloginPopup visible onClose={() => setLoginModalVisible(false)} onLogin={onLogin} />;
  }

  if (!visible) return null;

  return (
    <ModalPopup.Popup show onClose={onClose}>
      <ModalPopup.Header icon="warning">
        {
          expired ?
            t('authentication.user.prolong.session_expired') :
            t('authentication.user.prolong.session_about_to_expire')
        }
      </ModalPopup.Header>
      <ModalPopup.Content>
        <p className={styles.p}>
        {
          expired ?
            t('authentication.user.prolong.session_expired') :
            t('authentication.user.prolong.logg_out_countdown', { countdown: momentTimeLeft })
        }
        </p>
      </ModalPopup.Content>
      <ModalPopup.Actions hideCancel>
        <Button icon="cross" color={Button.COLOR.GRAY} small onClick={onLogout}>
            {t('base._client_logged_in_header.logout')}
        </Button>
        {
          !expired ? (
            <Button icon="arrow-right" color={Button.COLOR.PRIMARY} small onClick={onProlong} rightIcon>
                {t('authentication.user.prolong.stay_logged_in')}
            </Button>
          ) : (
            <Button
              icon="arrow-right"
              color={Button.COLOR.PRIMARY}
              small
              onClick={() => setLoginModalVisible(true)}
              rightIcon
            >
              {t('authentication.user._login.login')}
            </Button>
          )
        }
      </ModalPopup.Actions>
    </ModalPopup.Popup>
  );
}
